import { DisabledOptionInfo } from 'components/dropdown/Dropdown';
import { UpdateInterval } from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { collectionIntervalExceedsUpdateInterval, OptionChild } from 'utils/node';

/**
 * Only Timed mode has a concrete interval to validate against. SMARTsample and Triggered send
 * no UpdateInterval, and the V4 backend applies no collection-duration check to those modes, so
 * there is nothing to validate for them.
 */
export function getValidationUpdateIntervalMs(
    updateInterval: UpdateInterval,
): number | undefined {
    switch (updateInterval.mode) {
        case 'timed':
            return updateInterval.intervalMs;
        case 'smartsample':
        case 'triggered':
            return undefined;
        default:
            updateInterval satisfies never;
            return undefined;
    }
}

type CollectionDurationContext = Readonly<{
    samplingFrequencyHz: number | undefined;
    numberOfSamples: number | undefined;
    updateInterval: UpdateInterval;
}>;

export function collectionDurationExceedsInterval({
    samplingFrequencyHz,
    numberOfSamples,
    updateInterval,
}: CollectionDurationContext): boolean {
    return (
        collectionIntervalExceedsUpdateInterval({
            samplingFrequency: samplingFrequencyHz,
            numberOfSamples,
            updateInterval: getValidationUpdateIntervalMs(updateInterval),
        }) === true
    );
}

function buildDisabledOptions(
    options: OptionChild<number>[],
    isValueInvalid: (value: number) => boolean,
    message: string,
): DisabledOptionInfo[] {
    return options
        .filter((option) => isValueInvalid(option.Value))
        .map((option) => ({ key: option.Value, message }));
}

export function getDisabledSamplingFrequencyOptions(
    options: OptionChild<number>[],
    {
        numberOfSamples,
        updateInterval,
    }: Omit<CollectionDurationContext, 'samplingFrequencyHz'>,
): DisabledOptionInfo[] {
    const intervalMs = getValidationUpdateIntervalMs(updateInterval);
    return buildDisabledOptions(
        options,
        (samplingFrequency) =>
            collectionIntervalExceedsUpdateInterval({
                samplingFrequency,
                numberOfSamples,
                updateInterval: intervalMs,
            }) === true,
        'Collection duration is greater than update interval. Please adjust to save.',
    );
}

export function getDisabledNumberOfSamplesOptions(
    options: OptionChild<number>[],
    {
        samplingFrequencyHz,
        updateInterval,
    }: Omit<CollectionDurationContext, 'numberOfSamples'>,
): DisabledOptionInfo[] {
    const intervalMs = getValidationUpdateIntervalMs(updateInterval);
    return buildDisabledOptions(
        options,
        (numberOfSamples) =>
            collectionIntervalExceedsUpdateInterval({
                samplingFrequency: samplingFrequencyHz,
                numberOfSamples,
                updateInterval: intervalMs,
            }) === true,
        'Collection duration is greater than update interval. Please adjust to save.',
    );
}

export function getDisabledUpdateIntervalOptions(
    options: OptionChild<number>[],
    {
        samplingFrequencyHz,
        numberOfSamples,
    }: Pick<CollectionDurationContext, 'samplingFrequencyHz' | 'numberOfSamples'>,
): DisabledOptionInfo[] {
    return buildDisabledOptions(
        options,
        (intervalMs) =>
            collectionIntervalExceedsUpdateInterval({
                samplingFrequency: samplingFrequencyHz,
                numberOfSamples,
                updateInterval: intervalMs,
            }) === true,
        'Collection duration is greater than this interval. Please adjust to save.',
    );
}
